import React from 'react'
import { styled } from '@mui/material/styles'
import { NavigateFunction, BrowserRouter } from 'react-router-dom'
import Box from '@mui/material/Box'
import { getTopbarHeight, useDimensions } from '../../hooks/useDimensions'
import { useFirebase } from 'react-redux-firebase'
import { onIsLoading } from '../../utils/onLoading'
import { actions as appActions } from './reducer'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { Snackbar, Alert, Theme } from '@mui/material'
import { getAlert, getIsAdmin, getIsAuthenticated } from './selectors'
import { RootState, Store } from '../../store'
import { shouldFetchPortalData } from './auth'
import { AppRoutes } from './Routes'
import { getPortalBootstrapForce } from '../Profile/selectors/api/bootstrap'
import { useSetUnits } from './hooks/useSetUnits'
import { ADMIN_EMAIL, isDevelopmentEnvironment } from '../../config/getEnvVars'
import { clientLogger } from '../../utils/observability'
import { RouteChangeTracker } from './RouteChangeTracker'

const BoxComponent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  bgcolor: theme.palette.grey['900'],
}))

declare global {
  interface Window {
    navigate: NavigateFunction | undefined
    store: Store
    firebase: any
  }
}

function App() {
  const fetchTimeoutRef = React.useRef<number>()
  const store = useStore()
  const dispatch = useDispatch()
  const firebase = useFirebase()
  const { height, width } = useDimensions()
  const topbarHeight = getTopbarHeight(width)
  const isDevelopmentSite = React.useMemo(() => isDevelopmentEnvironment(), [])

  React.useEffect(() => {
    window.firebase = firebase
  }, [firebase])

  React.useEffect(() => {
    document.body.classList.toggle('development-site', isDevelopmentSite)

    return () => {
      document.body.classList.remove('development-site')
    }
  }, [isDevelopmentSite])

  const alert = useSelector(getAlert)
  const isAuthenticated = useSelector(getIsAuthenticated)
  const isAdminSelector = React.useMemo(() => getIsAdmin(ADMIN_EMAIL), [])
  const isAdmin = useSelector(isAdminSelector)

  React.useEffect(() => {
    if (!shouldFetchPortalData(isAuthenticated, isAdmin)) {
      clientLogger.debug('portal.bootstrap.skipped', {
        isAuthenticated,
        isAdmin,
      })
      onIsLoading(false)
      return
    }

    onIsLoading(true)
    fetchTimeoutRef.current = window.setTimeout(async () => {
      try {
        clientLogger.info('portal.bootstrap.started', {
          isAuthenticated,
          isAdmin,
        })
        const state = store.getState() as RootState
        await getPortalBootstrapForce(state)
        clientLogger.info('portal.bootstrap.completed')
      } catch (error) {
        clientLogger.error('portal.bootstrap.failed', { error })
      } finally {
        onIsLoading(false)
      }
    }, 2000)

    return () => {
      if (fetchTimeoutRef.current != null) {
        window.clearTimeout(fetchTimeoutRef.current)
      }
    }
  }, [isAuthenticated, isAdmin, store])

  useSetUnits()

  const onCloseAlert = React.useCallback(() => {
    dispatch(appActions.onAlert(undefined))
  }, [dispatch])

  return (
    <>
      <BoxComponent className="app-container">
        <BrowserRouter>
          <RouteChangeTracker />
          <AppRoutes isAuthenticated={isAuthenticated} isAdmin={isAdmin} />
        </BrowserRouter>
      </BoxComponent>
      <Snackbar
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(alert)}
        onClose={onCloseAlert}
      >
        <Alert
          onClose={onCloseAlert}
          severity={alert?.severity}
          sx={{ width: '100%' }}
        >
          {alert?.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default App
